import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { ArrowLeft, MapPin, Briefcase, Calendar, Euro, Clock, Cigarette, Dog, Volume2, Sparkles, Mail, Link as LinkIcon, Loader2, MessageCircle, ShieldCheck, Flag, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROOMMATE_PROFILE_STYLES = {
  Abierto:     'bg-pink-100 text-pink-700 border border-pink-200',
  Equilibrado: 'bg-orange-100 text-orange-700 border border-orange-200',
  Reservado:   'bg-violet-100 text-violet-700 border border-violet-200',
};

const OFFERS_ROOM_LABEL = {
  '1': '1 habitación',
  '2': '2 habitaciones',
  'more_than_2': 'Más de 2 habitaciones',
};

/* ── Timbre de casa animado ── */
function BellIcon() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, flexShrink: 0,
      animation: 'partyBell 2.2s ease-in-out infinite',
      transformOrigin: 'top center',
    }}>
      <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
        <path d="M3 10 L11 3 L19 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="6" y="10" width="10" height="8" rx="1" stroke="currentColor" strokeWidth="1.6" fill="transparent"/>
        <circle cx="11" cy="14" r="2" fill="currentColor"/>
        <circle cx="11" cy="14" r="1" fill="white"/>
        <rect x="13" y="6" width="2" height="3" rx="0.5" fill="currentColor"/>
      </svg>
    </span>
  );
}

/* ── Puerta animada ── */
function DoorIcon() {
  return (
    <span style={{ position: 'relative', width: 22, height: 22, flexShrink: 0, display: 'inline-flex' }}>
      <span style={{
        position: 'absolute', top: 3, left: 4, right: 4, bottom: 0,
        background: '#fef3c7', borderRadius: '4px 4px 0 0',
        animation: 'partyLight 4.5s ease-in-out 0.5s infinite',
      }} />
      <span style={{
        position: 'absolute', top: 3, left: 4, right: 4, bottom: 0,
        background: '#d4a574', borderRadius: '4px 4px 0 0',
        transformOrigin: 'left center',
        animation: 'partyDoor 4.5s ease-in-out 0.5s infinite',
        overflow: 'hidden',
      }}>
        <span style={{ position: 'absolute', top: 4, left: 3, right: 3, height: 5, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 2 }} />
        <span style={{ position: 'absolute', top: 12, left: 3, right: 3, height: 5, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 2 }} />
        <span style={{ position: 'absolute', right: 5, top: '52%', width: 3, height: 3, background: '#b8860b', borderRadius: '50%', transform: 'translateY(-50%)' }} />
      </span>
    </span>
  );
}

/* ── CSS keyframes ── */
const ANIM_STYLE = `
@keyframes partyBell {
  0%,100% { transform: rotate(0deg); }
  8%  { transform: rotate(22deg); }
  16% { transform: rotate(-22deg); }
  24% { transform: rotate(16deg); }
  32% { transform: rotate(-16deg); }
  40% { transform: rotate(0deg); }
}
@keyframes partyDoor {
  0%   { transform: perspective(400px) rotateY(0deg); }
  22%  { transform: perspective(400px) rotateY(-65deg); }
  42%  { transform: perspective(400px) rotateY(0deg); }
  62%  { transform: perspective(400px) rotateY(-65deg); }
  78%  { transform: perspective(400px) rotateY(0deg); }
  92%  { transform: perspective(400px) rotateY(-65deg); }
  100% { transform: perspective(400px) rotateY(-65deg); }
}
@keyframes partyLight {
  0%,21%  { opacity: 0; }
  22%,41% { opacity: 1; }
  42%,61% { opacity: 0; }
  62%,77% { opacity: 1; }
  78%,91% { opacity: 0; }
  92%,100%{ opacity: 1; }
}
@keyframes glowGreen {
  0%,100% { box-shadow: 0 0 0px 0px rgba(255,255,255,0); }
  50%      { box-shadow: 0 0 10px 4px rgba(255,255,255,0.55); }
}
@keyframes glowWhite {
  0%,100% { box-shadow: 0 0 0px 0px rgba(255,255,255,0); }
  50%      { box-shadow: 0 0 10px 4px rgba(255,255,255,0.6); }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('party-anims')) {
  const s = document.createElement('style');
  s.id = 'party-anims';
  s.textContent = ANIM_STYLE;
  document.head.appendChild(s);
}

export default function ProfileView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [blockConfirm, setBlockConfirm] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [wiggle, setWiggle] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profiles/${userId}`, {
          withCredentials: true,
          headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
        });
        setProfile(response.data);
        if (response.data.interest_status !== 'mutual' && response.data.interest_status !== 'sent') {
          setWiggle(true);
          setTimeout(() => setWiggle(false), 1000);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        navigate('/feed');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, navigate]);

  const handleInterestAction = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    setActionMessage('');
    const status = profile?.interest_status;
    try {
      if (status === 'sent') {
        await axios.delete(`${API}/interests/${userId}`, {
          withCredentials: true,
          headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
        });
        setActionMessage('Interés eliminado');
      } else {
        const response = await axios.post(`${API}/interests`, { target_user_id: userId }, {
          withCredentials: true,
          headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
        });
        if (response.data.is_match) {
          setActionMessage('¡Es un match! Ya podéis ver vuestros contactos');
        } else {
          setActionMessage('Interés enviado');
        }
      }
      const profileResponse = await axios.get(`${API}/profiles/${userId}`, {
        withCredentials: true,
        headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
      });
      setProfile(profileResponse.data);
    } catch (error) {
      setActionMessage(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async () => {
    try {
      await axios.post(`${API}/users/${userId}/block`, {}, {
        withCredentials: true,
        headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
      });
      navigate('/feed');
    } catch (err) {
      setActionMessage(err.response?.data?.detail || 'Error al bloquear');
    }
  };

  const handleReport = async () => {
    const reason = reportReason.trim();
    if (!reason) return;
    try {
      await axios.post(`${API}/users/${userId}/report`, { reason }, {
        withCredentials: true,
        headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) },
      });
      setActionMessage('Denuncia registrada');
      setReportOpen(false);
      setReportReason('');
    } catch (err) {
      setActionMessage(err.response?.data?.detail || 'Error al denunciar');
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.unsplash.com/photo-1760551937527-2bc6cfe45180?w=400';
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const hasCoverPhoto = !!profile?.cover_photo;

  const getButtonConfig = () => {
    const status = profile?.interest_status;
    if (actionLoading) return { text: 'Procesando...', className: 'bg-gray-400 text-white cursor-wait', style: {}, icon: null };
    if (status === 'mutual') return {
      text: 'Match mutuo',
      className: hasCoverPhoto ? 'bg-white text-[hsl(144,18%,35%)] border border-[hsl(144,18%,45%)] cursor-default' : 'bg-white text-[hsl(144,18%,35%)] border border-white/60 cursor-default',
      style: {}, icon: <DoorIcon />, disabled: true,
    };
    if (status === 'sent') return {
      text: 'Interés enviado',
      className: hasCoverPhoto ? 'bg-white text-[hsl(144,18%,35%)] border border-[hsl(144,18%,45%)] hover:bg-red-50 hover:text-red-500 hover:border-red-200' : 'bg-white text-[hsl(144,18%,35%)] border border-white/60 hover:bg-red-50 hover:text-red-500',
      style: {}, icon: <BellIcon />,
    };
    if (status === 'received') return {
      text: '¡Hacer match!',
      className: hasCoverPhoto ? 'bg-[hsl(144,18%,45%)] text-white hover:bg-[hsl(144,18%,38%)]' : 'bg-white text-[hsl(144,18%,35%)] border border-white/80 hover:bg-white/90',
      style: { animation: hasCoverPhoto ? 'glowWhite 2s ease-in-out infinite' : 'glowGreen 2s ease-in-out infinite' },
      icon: <DoorIcon />,
    };
    return {
      text: 'Me interesa',
      className: hasCoverPhoto ? 'bg-[hsl(144,18%,45%)] text-white hover:bg-[hsl(144,18%,38%)]' : 'bg-white text-[hsl(144,18%,35%)] border border-white/80 hover:bg-white/90',
      style: { animation: hasCoverPhoto ? 'glowWhite 2s ease-in-out infinite' : 'glowGreen 2s ease-in-out infinite' },
      icon: null,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(35,25%,91%)]">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const btnConfig = getButtonConfig();
  const roommateProfileStyle = profile.roommate_profile ? ROOMMATE_PROFILE_STYLES[profile.roommate_profile] : null;
  const isMutual = profile.interest_status === 'mutual';

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />Volver
        </button>

        {/* Profile Header */}
        <div className="rounded-2xl shadow-sm border border-[hsl(35,20%,85%)] overflow-hidden relative">
          <div className="absolute inset-0">
            {profile.cover_photo ? (
              <img src={profile.cover_photo} alt="Portada" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-[hsl(144,18%,45%)] to-[hsl(144,18%,55%)]" />
            )}
            <div className="absolute inset-0 bg-black/40" />
          </div>
          <div className="relative px-6 pt-6 pb-6">
            <div className="flex items-start justify-between">
              <img src={getPhotoUrl(profile.profile_photo)} alt={profile.name} className="w-28 h-28 rounded-2xl border-4 border-white object-cover shadow-lg" data-testid="profile-photo" />
              <div className="flex flex-col items-end gap-2">
                {isMutual && (
                  <Link
                    to={`/chat/${userId}`}
                    className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 ${
                      hasCoverPhoto
                        ? 'bg-[hsl(144,18%,45%)] text-white hover:bg-[hsl(144,18%,38%)]'
                        : 'bg-white text-[hsl(144,18%,35%)] border border-white/80 hover:bg-white/90'
                    }`}
                    data-testid="open-chat-btn"
                  >
                    <MessageCircle className="w-4 h-4" /><span translate="no">Chat</span>
                  </Link>
                )}
                <button
                  type="button"
                  onClick={btnConfig.disabled ? undefined : handleInterestAction}
                  disabled={actionLoading || btnConfig.disabled}
                  style={btnConfig.style || {}}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 ${btnConfig.className} ${wiggle ? 'animate-wiggle' : ''}`}
                  data-testid="interest-action-btn"
                  translate="no"
                >
                  {btnConfig.icon}
                  {btnConfig.text}
                </button>
                {actionMessage && (
                  <p className="text-sm text-gray-600 bg-white/80 px-3 py-1.5 rounded-lg text-right" data-testid="action-message">{actionMessage}</p>
                )}
              </div>
            </div>

            <div className="mt-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading font-bold text-2xl text-gray-900" data-testid="profile-name">
                  {profile.name}{profile.age ? `, ${profile.age}` : ''}
                </h1>
                {profile.email_verified && (
                  <span className="inline-flex items-center gap-1 bg-[hsl(144,18%,93%)] text-[hsl(144,18%,35%)] text-xs font-medium px-2 py-0.5 rounded-full">
                    <ShieldCheck className="w-3 h-3" />Verificado
                  </span>
                )}
                {roommateProfileStyle && (
                  <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${roommateProfileStyle}`}>
                    🏠 {profile.roommate_profile}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-gray-600">
                {profile.city && <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /><span>{profile.city}</span></div>}
                {profile.occupation && <div className="flex items-center gap-1"><Briefcase className="w-4 h-4" /><span>{profile.occupation}</span></div>}
                {profile.nationality && <div className="flex items-center gap-1 text-sm"><span>{profile.nationality}</span></div>}
                {profile.lgbtq_friendly && <span className="text-sm" title="LGBTQ+ Friendly">🏳️‍🌈</span>}
                {profile.erasmus && <span className="text-sm text-gray-600" title="Erasmus">✈️ Erasmus</span>}
              </div>
              {profile.bio && <p className="mt-4 text-gray-600" data-testid="profile-bio">{profile.bio}</p>}
              {profile.activity_tags && profile.activity_tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {profile.activity_tags.map(tag => (
                    <span key={tag} className="bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium px-2.5 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              )}
              {profile.interest_status === 'received' && (
                <p className="mt-4 text-sm text-[hsl(144,18%,30%)] bg-[hsl(144,18%,93%)] px-3 py-2 rounded-lg">
                  Esta persona ha mostrado interés en ti
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        {profile.can_see_contact && (
          <div className="bg-green-50 rounded-2xl border border-green-100 p-6 mt-6">
            <h2 className="font-heading font-semibold text-lg text-green-800 mb-4">Información de contacto</h2>
            <div className="space-y-3">
              {(profile.contact_email || profile.email) && (
                <a href={`mailto:${profile.contact_email || profile.email}`} className="flex items-center gap-3 text-green-700 hover:text-green-800" data-testid="contact-email">
                  <Mail className="w-5 h-5" /><span>{profile.contact_email || profile.email}</span>
                </a>
              )}
              {profile.contact_link && (
                <a href={profile.contact_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-green-700 hover:text-green-800" data-testid="contact-link">
                  <LinkIcon className="w-5 h-5" /><span>{profile.contact_link}</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Details Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Detalles de búsqueda</h2>
            <div className="space-y-3">
              {profile.budget && <div className="flex items-center gap-3 text-gray-600"><Euro className="w-5 h-5 text-gray-400" /><span>Presupuesto: <strong>{profile.budget}€/mes</strong></span></div>}
              {profile.move_in_date && <div className="flex items-center gap-3 text-gray-600"><Calendar className="w-5 h-5 text-gray-400" /><span>Entrada: <strong>{profile.move_in_date}</strong></span></div>}
              {profile.stay_duration && <div className="flex items-center gap-3 text-gray-600"><Clock className="w-5 h-5 text-gray-400" /><span>Duración: <strong>{profile.stay_duration}</strong></span></div>}
              {profile.offers_room && (
                <div className="flex items-center gap-3 text-[hsl(144,18%,35%)]">
                  <span className="text-lg leading-none">🏠</span>
                  <span>Ofrece habitación: <strong>{OFFERS_ROOM_LABEL[profile.offers_room]}</strong></span>
                </div>
              )}
              {profile.preferences && <div className="mt-4"><p className="text-sm text-gray-500 mb-2">Preferencias:</p><p className="text-gray-600">{profile.preferences}</p></div>}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Estilo de vida</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {profile.is_smoker !== undefined && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${profile.is_smoker ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                  <Cigarette className="w-3 h-3" />{profile.is_smoker ? 'Fumador' : 'No fumador'}
                </span>
              )}
              {profile.has_pets !== undefined && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${profile.has_pets ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                  <Dog className="w-3 h-3" />{profile.has_pets ? 'Con mascotas' : 'Sin mascotas'}
                </span>
              )}
            </div>
            <div className="space-y-3 text-gray-600">
              {profile.cleaning_habits && <div className="flex items-center gap-3"><Sparkles className="w-5 h-5 text-gray-400" /><span>Limpieza: <strong>{profile.cleaning_habits}</strong></span></div>}
              {profile.sociability && <div className="flex items-center gap-3"><span className="w-5 h-5 text-gray-400 text-center text-sm">S</span><span>Sociabilidad: <strong>{profile.sociability}</strong></span></div>}
              {profile.noise_habits && <div className="flex items-center gap-3"><Volume2 className="w-5 h-5 text-gray-400" /><span>Ruido: <strong>{profile.noise_habits}</strong></span></div>}
              {profile.schedule && <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-gray-400" /><span>Horario: <strong>{profile.schedule}</strong></span></div>}
            </div>
          </div>
        </div>

        {/* Acepta */}
        {(profile.accepts_smokers !== undefined || profile.accepts_pets !== undefined) && (
          <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6 mt-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Acepta</h2>
            <div className="flex flex-wrap gap-3">
              {profile.accepts_smokers !== undefined && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {profile.accepts_smokers ? '✓ Acepta fumadores' : '✗ No acepta fumadores'}
                </span>
              )}
              {profile.accepts_pets !== undefined && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {profile.accepts_pets ? '✓ Acepta mascotas' : '✗ No acepta mascotas'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Report & Block */}
        <div className="mt-8 pt-6 border-t border-[hsl(35,20%,85%)] flex items-center gap-4">
          {!reportOpen && !blockConfirm && (
            <>
              <button onClick={() => setReportOpen(true)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600" data-testid="report-btn">
                <Flag className="w-3.5 h-3.5" />Denunciar perfil
              </button>
              <button onClick={() => setBlockConfirm(true)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500" data-testid="block-btn">
                <Ban className="w-3.5 h-3.5" />Bloquear usuario
              </button>
            </>
          )}
          {blockConfirm && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">¿Bloquear a {profile.name}? No podrá verte ni contactarte.</span>
              <button onClick={handleBlock} className="text-xs px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600" data-testid="confirm-block-btn">Sí, bloquear</button>
              <button onClick={() => setBlockConfirm(false)} className="text-xs px-3 py-1 bg-gray-200 text-gray-600 rounded-lg">Cancelar</button>
            </div>
          )}
          {reportOpen && (
            <div className="flex-1 space-y-2">
              <p className="text-xs text-gray-500">Motivo de la denuncia:</p>
              <div className="flex gap-2">
                <input type="text" value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Describe el problema..." className="flex-1 text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(144,18%,45%)]" data-testid="report-reason-input" />
                <button onClick={handleReport} disabled={!reportReason.trim()} className="text-xs px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50" data-testid="submit-report-btn">Enviar</button>
                <button onClick={() => { setReportOpen(false); setReportReason(''); }} className="text-xs px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg">Cancelar</button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
